import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { cx } from "shared/utils";
import { useState, useRef } from "react";
import { Show } from "shared/components/helpers/Show";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Trash02 } from "untitledui-js-base";

const VALID_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg"];

interface LogoDropZoneProps {
  disabled?: boolean;
  className?: string;
  logoUrl?: string;
  initialFile?: File | null;
  onFileSelect?: (file: File | null) => void;
  isUploading?: boolean;
}

export const LogoDropZone = ({
  logoUrl,
  disabled = false,
  className,
  initialFile = null,
  onFileSelect,
  isUploading = false
}: LogoDropZoneProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const [selectedFile, setSelectedFile] = useState<File | null>(initialFile);
  const [previewUrl, setPreviewUrl] = useState<string | null>(logoUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      if (onFileSelect) onFileSelect(file);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    setPreviewUrl(null);
    onFileSelect?.(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="relative">
      <label
        tabIndex={0}
        className={cx(
          "animate-appear relative h-[118px] w-full flex items-center justify-center gap-2 rounded-lg border border-alpha-100",
          !previewUrl &&
            "border-dashed bg-background-900 hover:bg-background-800",
          disabled && "opacity-50 cursor-not-allowed",
          isUploading && "opacity-70",
          className
        )}
      >
        <Show if={!previewUrl}>
          <div className="flex flex-col items-center justify-center gap-1 pb-6 pt-5">
            <p className="text-sm font-medium text-center text-content-400">
              Upload {dictionary.property} logo
            </p>
            <p className="text-sm text-center text-content-200">(Optional)</p>
          </div>
        </Show>

        {previewUrl ? (
          <img
            src={previewUrl}
            alt={selectedFile?.name || "Uploaded logo"}
            className="h-full object-contain"
          />
        ) : null}

        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="flex flex-col items-center gap-1">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-white text-xs font-medium">
                Uploading...
              </span>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          accept={VALID_FILE_TYPES.join(",")}
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={handleFileChange}
          required={false}
          type="file"
          disabled={disabled || isUploading}
        />
      </label>

      <Show if={previewUrl && !isUploading}>
        <div className="absolute right-3 bottom-2" onClick={handleRemoveFile}>
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.PRIMARY}
            Icon={Trash02}
            disabled={disabled || isUploading}
            className="backdrop-blur-md !bg-alpha-600 hover:!bg-alpha-500 transition-colors"
          />
        </div>
      </Show>
    </div>
  );
};
